import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { gte } from 'tio-ui/utilities';
import HighlightCard from 'tio-employee/components/reimagined/highlight-card';
import {
  PhSun,
  PhMoon,
  PhCloudSun,
  PhTrophy,
  PhTrendUp,
  PhTrendDown,
  PhTarget,
  PhLightbulb,
  PhArrowRight,
  PhCompass,
  PhChatCircleDots,
  PhCalculator,
  PhScales,
  PhPiggyBank,
  PhGraduationCap,
  PhBookOpen,
  PhCaretRight,
  PhHandshake,
  PhPath,
  PhRobot,
  PhBank,
  PhReceipt,
  PhChartLineUp,
  PhBuildings,
  PhHeart,
  PhShieldCheck,
  PhFileText,
  PhSparkle,
  PhRocket,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  type DemoScenario,
} from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import MetricCard from 'tio-employee/components/reimagined/metric-card';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import BenefitCard from 'tio-employee/components/reimagined/benefit-card';
import StepCard from 'tio-employee/components/reimagined/step-card';
import SectionHeader from 'tio-employee/components/reimagined/section-header';
import type { BenefitOpportunity } from 'tio-employee/components/reimagined/benefit-card';
import MomentumPanel from 'tio-employee/components/reimagined/momentum-panel';
import ToolCard from 'tio-employee/components/reimagined/tool-card';
import type {
  EmployeeCareerProfile,
  CareerPlan,
  Recommendation,
  CareerSummary,
  CareerCapitalScore,
  FinancialSnapshot,
  EmployerContributions,
} from 'tio-employee/services/career-pathways-api';
import type {
  DataSourceInfo,
  LivePslfData,
  LiveTasData,
} from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';

interface HomeModel {
  employee: EmployeeCareerProfile;
  careerPlan: CareerPlan;
  recommendations: Recommendation[];
  summary: CareerSummary;
  score: CareerCapitalScore;
  financialSnapshot: FinancialSnapshot;
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  pslfData: LivePslfData;
  tasData: LiveTasData;
  isLiveData: boolean;
}

interface CareerPathwaysHomeSignature {
  Args: {
    model: HomeModel;
  };
}

// Financial summary - user doesn't care which "product" this comes from
interface UnifiedFinancials {
  totalLoans: number;
  monthlyPayment: number;
  employerHelpYTD: number;
  employerHelpLifetime: number;
  projectedForgiveness: number;
  taxSavingsYTD: number;
  yearsToPayoff: number;
  interestRate: number;
}

export default class CareerPathwaysHome extends Component<CareerPathwaysHomeSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;
  @tracked showAllActions = false;

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario(): DemoScenario {
    return getCurrentDemoScenario();
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  /**
   * Data source helpers for the DataIndicator component
   */
  get studentLoansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.employerContributions === 'live' ? 'live' : 'placeholder';
  }

  get section127Source(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.section127Used === 'live' ? 'live' : 'placeholder';
  }

  get careerScoreSource(): DataSource {
    // Career score is always placeholder until backend built
    if (this.isDemoMode) return 'demo';
    return 'placeholder';
  }

  get recommendationsSource(): DataSource {
    // Recommendations are always placeholder until AI service built
    if (this.isDemoMode) return 'demo';
    return 'placeholder';
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  // Feature flags from current scenario
  get showTuitionAssistance() {
    if (this.isDemoMode) return this.scenario.features.tuitionAssistance;
    return (this.employee.taBenefit?.annualMax ?? 0) > 0;
  }

  get showStudentLoans() {
    if (this.isDemoMode) return this.scenario.features.studentLoanRepayment;
    return this.financialSnapshot.studentLoans.totalBalance > 0;
  }

  get showCareerPathways() {
    if (this.isDemoMode) return this.scenario.features.careerPathways;
    return true;
  }

  get isFirstLogin() {
    if (this.isDemoMode) return this.scenario.userState.isFirstLogin;
    return (this.summary.profileCompleteness ?? 0) < 20;
  }

  get isBenefitsMaxed() {
    if (this.isDemoMode) return this.scenario.userState.benefitsMaxedOut;
    return (this.financialSnapshot.section127.remaining ?? 0) <= 0;
  }

  get scenarioMessaging() {
    if (this.isDemoMode) return this.scenario.messaging;
    return {
      welcomeTitle: `Welcome back, ${this.firstName}!`,
      welcomeSubtitle: "Here's what's new in your benefits this week",
      ctaText: 'Get started',
    };
  }

  get employee() {
    return this.args.model.employee;
  }

  get careerPlan() {
    return this.args.model.careerPlan;
  }

  get score() {
    return this.args.model.score;
  }

  get financialSnapshot() {
    return this.args.model.financialSnapshot;
  }

  get summary() {
    return this.args.model.summary;
  }

  get recommendations() {
    return this.args.model.recommendations;
  }

  get firstName() {
    return this.employee.firstName;
  }

  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }

  get greetingIcon() {
    const hour = new Date().getHours();
    if (hour < 12) return PhSun;
    if (hour < 17) return PhCloudSun;
    return PhMoon;
  }

  get financials(): UnifiedFinancials {
    const loans = this.financialSnapshot.studentLoans;
    const contributions = this.financialSnapshot.employerContributions;

    return {
      totalLoans: loans.totalBalance,
      monthlyPayment: loans.monthlyPayment,
      employerHelpYTD: contributions.yearToDate,
      employerHelpLifetime: contributions.lifetime,
      projectedForgiveness: this.pslfEligible ? this.pslfEstimatedForgiveness : 0,
      taxSavingsYTD: this.financialSnapshot.optimizationPotential.taxSavingsYtd,
      yearsToPayoff: this.calculateYearsToPayoff(loans.estimatedPayoffDate),
      interestRate: loans.interestRate,
    };
  }

  calculateYearsToPayoff(dateStr: string) {
    const payoffDate = new Date(dateStr);
    const now = new Date();
    const diffYears = (payoffDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 365);
    return Math.round(diffYears * 10) / 10;
  }

  // Live PSLF and TAS data from model
  get pslfData() {
    return this.args.model.pslfData;
  }

  get tasData() {
    return this.args.model.tasData;
  }

  // PSLF requires BOTH: qualifying employer AND student loan feature enabled
  get pslfEligible() {
    if (this.isDemoMode) {
      return this.showStudentLoans && this.demoClient.client.id === 'advocate';
    }
    // Use live PSLF data - either enrolled or eligible
    return this.pslfData.isEligible || this.pslfData.isEnrolled;
  }

  get pslfEnrolled() {
    if (this.isDemoMode) return this.pslfEligible;
    return this.pslfData.isEnrolled;
  }

  get pslfQualifyingPayments() {
    return this.pslfData.qualifyingPayments;
  }

  get pslfEstimatedForgiveness() {
    if (this.isDemoMode) return 45000;
    return this.pslfData.estimatedForgiveness;
  }

  get slrEnrolled() {
    return this.showStudentLoans;
  }

  get tasEnrolled() {
    if (this.isDemoMode) return this.showTuitionAssistance;
    // Use live TAS data - check if user has active enrollments or applications
    return (
      this.tasData.programs.length > 0 ||
      this.tasData.applications.length > 0 ||
      this.tasData.summary.activeCount > 0
    );
  }

  get tasPendingApplications() {
    return this.tasData.summary.pendingCount;
  }

  get tasApprovedAmount() {
    return this.tasData.summary.totalApproved;
  }

  get benefitOpportunities(): BenefitOpportunity[] {
    const opportunities: BenefitOpportunity[] = [];
    const section127 = this.financialSnapshot.section127;

    // Only show student loan benefit if enabled in scenario
    if (this.showStudentLoans) {
      opportunities.push({
        id: 'slr',
        title: 'Student Loan Payments',
        description: `${this.employerName} pays toward your student loans`,
        value: `$${section127.loanAllocation.toLocaleString()}/year`,
        enrolled: this.slrEnrolled,
        eligible: true,
        icon: PhBank,
      });
    }

    // Only show tuition assistance if enabled in scenario
    if (this.showTuitionAssistance) {
      opportunities.push({
        id: 'tas',
        title: 'Tuition Assistance',
        description: 'Get help paying for courses and certifications',
        value: `$${section127.tuitionAllocation.toLocaleString()}/year`,
        enrolled: this.tasEnrolled,
        eligible: true,
        icon: PhGraduationCap,
      });
    }

    // PSLF requires student loans enabled + qualifying employer
    if (this.pslfEligible) {
      opportunities.push({
        id: 'pslf',
        title: 'Loan Forgiveness (PSLF)',
        description: 'Work at a qualifying employer = loans forgiven after 10 years',
        value: 'Up to 100% forgiven',
        enrolled: true,
        eligible: true,
        icon: PhShieldCheck,
      });
    }

    // Tax-free benefit shows if either TA or SLRP is enabled
    if (this.showTuitionAssistance || this.showStudentLoans) {
      opportunities.push({
        id: 'section127',
        title: 'Tax-Free Education Benefit',
        description: 'Up to $5,250/year in tax-free education assistance',
        value: `$${section127.remaining.toLocaleString()} remaining`,
        deadline: 'Dec 31',
        enrolled: true,
        eligible: true,
        icon: PhReceipt,
      });
    }

    return opportunities;
  }

  get smartActions(): SmartAction[] {
    const actions: SmartAction[] = [];
    const section127 = this.financialSnapshot.section127;

    // Section 127 benefit reminder - applicable to both TA and SLRP
    if (section127.remaining > 2000) {
      actions.push({
        id: 'use-benefits',
        priority: 'urgent',
        category: 'money',
        icon: PhPiggyBank,
        title: `You have ${this.formatCurrency(section127.remaining)} in unused benefits`,
        description: "Tax-free employer money expires Dec 31 if you don't use it",
        impact: `Save ${this.formatCurrency(section127.remaining * 0.25)} in taxes`,
        cta: 'Use my benefits',
        route: 'authenticated.reimagined.finance',
      });
    }

    // TAS - pending applications action
    if (this.tasPendingApplications > 0) {
      actions.push({
        id: 'tas-pending',
        priority: 'high',
        category: 'money',
        icon: PhGraduationCap,
        title: `${this.tasPendingApplications} tuition request${this.tasPendingApplications > 1 ? 's' : ''} pending`,
        description: 'Check the status of your tuition assistance applications',
        cta: 'View status',
        route: 'authenticated.reimagined.benefits',
      });
    }

    // PSLF - only if student loans enabled AND employer qualifies
    if (this.pslfEligible) {
      const payments = this.pslfQualifyingPayments;
      const remaining = 120 - payments;
      actions.push({
        id: 'pslf-cert',
        priority: remaining <= 12 ? 'urgent' : 'high',
        category: 'deadline',
        icon: PhFileText,
        title: remaining <= 12 ? 'Almost there! Keep certifying' : 'Annual PSLF certification due',
        description: 'Certify your qualifying payments to stay on track for forgiveness',
        impact: `${payments} of 120 payments certified`,
        cta: 'Certify now',
        route: 'authenticated.pslf',
      });
    }

    // Optimization - only if student loans enabled (requires both TA and SLRP to optimize between)
    if (this.showStudentLoans && this.showTuitionAssistance) {
      const recommendation = this.financialSnapshot.optimizationPotential;
      if (recommendation.potentialSavings > 1000) {
        actions.push({
          id: 'optimize',
          priority: 'high',
          category: 'money',
          icon: PhChartLineUp,
          title: `Save ${this.formatCurrency(recommendation.potentialSavings)} more per year`,
          description: 'AI found a better way to split your benefits between loans and tuition',
          impact: `Pay off ${recommendation.projectedPayoffAcceleration} faster`,
          cta: 'See optimization',
          route: 'authenticated.reimagined.finance.optimizer',
        });
      }
    }

    // Career milestone progress - always show
    const inProgress = this.careerPlan?.milestones?.find((m) => m.status === 'in-progress');
    if (inProgress) {
      actions.push({
        id: 'continue-learning',
        priority: 'medium',
        category: 'career',
        icon: PhBookOpen,
        title: `Continue: ${inProgress.title}`,
        description: `${inProgress.progress}% complete - you're making great progress!`,
        cta: 'Keep learning',
        route: 'authenticated.reimagined.plan',
      });
    }

    // Skills assessment - always show
    if (this.summary.skillsTracked < 5) {
      actions.push({
        id: 'skills',
        priority: 'medium',
        category: 'career',
        icon: PhLightbulb,
        title: 'Discover your skills',
        description: 'Take a quick assessment to unlock personalized recommendations',
        impact: '+15 career score points',
        cta: 'Start assessment',
        route: 'authenticated.reimagined.skills.assessment',
      });
    }

    // Career goal - always show
    if (!this.employee.targetRole) {
      actions.push({
        id: 'set-goal',
        priority: 'low',
        category: 'career',
        icon: PhTarget,
        title: 'Set a career goal',
        description: 'See what roles match your skills and interests',
        cta: 'Explore careers',
        route: 'authenticated.reimagined.explore',
      });
    }

    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    return actions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }

  get visibleActions() {
    return this.showAllActions ? this.smartActions : this.smartActions.slice(0, 4);
  }

  get progressPercent() {
    return this.careerPlan?.progress ?? 0;
  }

  get progressStyle() {
    return htmlSafe(`width: ${this.progressPercent}%`);
  }

  toggleShowAll = () => {
    this.showAllActions = !this.showAllActions;
  };

  formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 max-w-6xl mx-auto">
      {{#if this.isFirstLogin}}
        {{! FIRST LOGIN EXPERIENCE }}
        <div
          class="bg-gradient-to-br from-violet-600 via-indigo-600 to-purple-700 rounded-2xl p-8 text-white mb-6 relative overflow-hidden"
        >
          <div class="absolute inset-0 opacity-10">
            <svg class="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="confetti" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="5" cy="5" r="2" fill="white" />
                  <circle cx="15" cy="15" r="1.5" fill="white" />
                  <circle cx="10" cy="18" r="1" fill="white" />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#confetti)" />
            </svg>
          </div>

          <div class="relative z-10 text-center max-w-2xl mx-auto py-8">
            <div
              class="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <PhSparkle @size={{40}} class="text-white" />
            </div>

            <Heading @level={{1}} class="text-white text-3xl md:text-4xl font-bold mb-4">
              {{this.scenarioMessaging.welcomeTitle}}
            </Heading>

            <Text class="text-white/90 text-lg mb-8">
              {{this.scenarioMessaging.welcomeSubtitle}}
            </Text>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <LinkTo
                @route="authenticated.reimagined.index"
                class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-violet-700 font-semibold rounded-xl hover:bg-white/90 transition-colors"
              >
                <PhRocket @size={{20}} />
                <span>{{this.scenarioMessaging.ctaText}}</span>
              </LinkTo>
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/20 text-white font-semibold rounded-xl hover:bg-white/30 transition-colors"
              >
                <PhCompass @size={{20}} />
                <span>Take a Tour</span>
              </button>
            </div>
          </div>
        </div>

        {{! ONBOARDING CHECKLIST FOR FIRST LOGIN }}
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div class="flex items-center gap-3 mb-4">
            <div class="p-2 bg-violet-100 rounded-lg">
              <PhTarget @size={{24}} class="text-violet-600" />
            </div>
            <div>
              <Heading @level={{2}} class="text-lg font-semibold text-gray-900">
                Getting Started Checklist
              </Heading>
              <Text @intent="muted" class="text-sm">Complete these steps to unlock your benefits</Text>
            </div>
          </div>

          <div class="space-y-3">
            <StepCard
              @step={{1}}
              @title="Link your student loans"
              @description="Connect your loan servicer to see your balances and get employer contributions"
            >
              <:action>
                <Button @intent="primary" @appearance="outlined">Connect</Button>
              </:action>
            </StepCard>

            <StepCard
              @step={{2}}
              @title="Complete your career profile"
              @description="Tell us about your skills and goals for personalized recommendations"
            >
              <:action>
                <Button @intent="primary" @appearance="outlined">Start</Button>
              </:action>
            </StepCard>

            <StepCard
              @step={{3}}
              @title="Explore your education benefits"
              @description="See what programs you're eligible for and how much you can save"
            >
              <:action>
                <Button @intent="primary" @appearance="outlined">Explore</Button>
              </:action>
            </StepCard>
          </div>
        </div>

        {{! BENEFIT HIGHLIGHTS FOR FIRST LOGIN }}
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {{#if this.showTuitionAssistance}}
            <HighlightCard
              @icon={{PhGraduationCap}}
              @title="Tuition Assistance"
              @value="$5,250/year"
              @description="Tax-free education funding for degrees, certificates, and more"
              @color="emerald"
            />
          {{/if}}

          {{#if this.showStudentLoans}}
            <HighlightCard
              @icon={{PhBank}}
              @title="Loan Repayment"
              @value="$125/month"
              @description="Your employer pays directly toward your student loans"
              @color="blue"
            />
          {{/if}}

          {{#if this.showCareerPathways}}
            <HighlightCard
              @icon={{PhPath}}
              @title="Career Pathways"
              @value="AI-Powered"
              @description="Personalized career guidance with funded skill development"
              @color="violet"
            />
          {{/if}}
        </div>

      {{else if this.isBenefitsMaxed}}
        {{! MAXED OUT SCENARIO }}
        <div
          class="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 rounded-2xl p-8 text-white mb-6 relative overflow-hidden"
        >
          <div class="relative z-10">
            <div class="flex items-center gap-3 mb-4">
              <div class="p-3 bg-white/20 rounded-xl">
                <PhTrophy @size={{32}} class="text-white" />
              </div>
              <div>
                <Heading @level={{1}} class="text-white text-2xl md:text-3xl font-bold">
                  {{this.scenarioMessaging.welcomeTitle}}
                </Heading>
                <Text class="text-white/90">
                  {{this.scenarioMessaging.welcomeSubtitle}}
                </Text>
              </div>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div class="bg-white/10 rounded-xl p-4 text-center">
                <div class="text-3xl font-bold">$5,250</div>
                <div class="text-sm text-white/80">Tuition Used</div>
              </div>
              <div class="bg-white/10 rounded-xl p-4 text-center">
                <div class="text-3xl font-bold">$1,500</div>
                <div class="text-sm text-white/80">Loan Help</div>
              </div>
              <div class="bg-white/10 rounded-xl p-4 text-center">
                <div class="text-3xl font-bold">12</div>
                <div class="text-sm text-white/80">Courses Done</div>
              </div>
              <div class="bg-white/10 rounded-xl p-4 text-center">
                <div class="text-3xl font-bold">$1,200</div>
                <div class="text-sm text-white/80">Tax Savings</div>
              </div>
            </div>

            <div class="mt-6 p-4 bg-white/10 rounded-xl">
              <div class="flex items-center justify-between">
                <div>
                  <div class="font-semibold">Benefits reset January 1, 2027</div>
                  <div class="text-sm text-white/80">Plan your next year now!</div>
                </div>
                <LinkTo
                  @route="authenticated.reimagined.plan"
                  class="px-4 py-2 bg-white text-orange-600 font-semibold rounded-lg hover:bg-white/90"
                >
                  Plan for 2027
                </LinkTo>
              </div>
            </div>
          </div>
        </div>

      {{else}}
        {{! STANDARD HERO: Financial Snapshot + Greeting }}
        <DataIndicator
          @source={{this.studentLoansSource}}
          @showBadge={{true}}
          @dataSourceId="student-loans"
        >
          <div
            class="bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800 rounded-2xl p-8 text-white mb-6 relative overflow-hidden"
          >
            <div class="absolute inset-0 opacity-10">
              <svg class="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="dots" width="10" height="10" patternUnits="userSpaceOnUse">
                    <circle cx="5" cy="5" r="1" fill="white" />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#dots)" />
              </svg>
            </div>

            <div class="relative z-10">
              <div class="flex items-center gap-2 text-white/80 mb-1">
                {{#let this.greetingIcon as |GreetingIcon|}}
                  <GreetingIcon @size={{18}} />
                {{/let}}
                <span class="text-sm">{{this.greeting}}, {{this.firstName}}</span>
              </div>

              <Heading @level={{1}} class="text-white text-2xl md:text-3xl font-bold mb-6">
                {{#if this.showStudentLoans}}
                  Your Financial Wellness Dashboard
                {{else}}
                  Your Career & Education Dashboard
                {{/if}}
              </Heading>

              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {{#if this.showStudentLoans}}
                  {{! Loan-related metrics }}
                  <MetricCard
                    @icon={{PhBank}}
                    @label="Student Loans"
                    @value={{this.formatCurrency this.financials.totalLoans}}
                    @subtext="{{this.financials.yearsToPayoff}} years to payoff"
                  >
                    <:trend>
                      <PhTrendDown @size={{14}} class="text-emerald-300" />
                    </:trend>
                  </MetricCard>

                  <MetricCard
                    @icon={{PhHandshake}}
                    @label="Employer Help YTD"
                    @value={{this.formatCurrency this.financials.employerHelpYTD}}
                    @valueClass="text-emerald-300"
                    @subtext="{{this.formatCurrency this.financials.employerHelpLifetime}} lifetime"
                  />
                {{else}}
                  {{! Education-focused metrics for TA-only }}
                  <MetricCard
                    @icon={{PhGraduationCap}}
                    @label="Tuition Available"
                    @value="$5,250"
                    @subtext="Annual tax-free benefit"
                    @valueClass="text-emerald-300"
                  />

                  <MetricCard
                    @icon={{PhBookOpen}}
                    @label="Courses Completed"
                    @value="3"
                    @subtext="This year"
                  >
                    <:trend>
                      <PhTrendUp @size={{14}} class="text-emerald-300" />
                    </:trend>
                  </MetricCard>
                {{/if}}

                <MetricCard
                  @icon={{PhReceipt}}
                  @label="Tax Savings YTD"
                  @value={{this.formatCurrency this.financials.taxSavingsYTD}}
                  @valueClass="text-amber-300"
                  @subtext="From Section 127 benefit"
                />

                {{#if this.pslfEligible}}
                  <MetricCard
                    @icon={{PhShieldCheck}}
                    @label="Projected Forgiveness"
                    @value={{this.formatCurrency this.financials.projectedForgiveness}}
                    @valueClass="text-violet-300"
                    @subtext="84/120 payments • 3 years left"
                  />
                {{else}}
                  <MetricCard
                    @icon={{PhTrophy}}
                    @label="Career Score"
                    @value="{{this.score.totalScore}}"
                    @subtext="Top {{this.score.percentile}}%"
                  >
                    <:trend>
                      <PhTrendUp @size={{14}} class="text-emerald-300" />
                    </:trend>
                  </MetricCard>
                {{/if}}
              </div>
            </div>
          </div>
        </DataIndicator>
      {{/if}}

      {{! SMART ACTIONS - What should they do NOW? }}
      <DataIndicator
        @source={{this.recommendationsSource}}
        @showBadge={{true}}
        @dataSourceId="ai-recommendations"
        class="mb-6"
      >
        <div>
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <PhCompass @size={{24}} class="text-violet-600 dark:text-violet-400" />
              <Heading @level={{2}} class="text-xl font-semibold text-gray-900 dark:text-white">
                What to Do Next
              </Heading>
            </div>
            <Text @intent="muted" class="text-sm dark:text-gray-400">
              {{this.smartActions.length}}
              personalized actions
            </Text>
          </div>

          <div class="space-y-3">
            {{#each this.visibleActions as |smartAction|}}
              <SmartActionCard @item={{smartAction}} />
            {{/each}}
          </div>

          {{#if (gte this.smartActions.length 5)}}
            <button
              type="button"
              class="w-full mt-3 py-3 text-center text-violet-600 hover:text-violet-700 font-medium text-sm flex items-center justify-center gap-2"
              {{on "click" this.toggleShowAll}}
            >
              {{#if this.showAllActions}}
                Show fewer
              {{else}}
                Show all
                {{this.smartActions.length}}
                actions
                <PhCaretRight @size={{16}} />
              {{/if}}
            </button>
          {{/if}}
        </div>
      </DataIndicator>

      {{! YOUR BENEFITS }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
        class="mb-6"
      >
        <div
          class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <PhHeart @size={{24}} class="text-pink-500 dark:text-pink-400" />
              <Heading @level={{2}} class="text-lg font-semibold text-gray-900 dark:text-white">
                Your
                {{this.employerName}}
                Benefits
              </Heading>
            </div>
            <Chip @intent="success" class="text-xs">
              {{this.benefitOpportunities.length}}
              active
            </Chip>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{#each this.benefitOpportunities as |benefit|}}
              <BenefitCard @benefit={{benefit}} />
            {{/each}}
          </div>
        </div>
      </DataIndicator>

      {{! BOTTOM GRID: Career + Quick Links }}
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <DataIndicator
          @source={{this.careerScoreSource}}
          @showBadge={{true}}
          @dataSourceId="career-capital-score"
          class="lg:col-span-2"
        >
          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <PhPath @size={{24}} class="text-violet-600 dark:text-violet-400" />
                <Heading
                  @level={{2}}
                  class="text-lg font-semibold text-gray-900 dark:text-white"
                >Career Growth</Heading>
              </div>
              <LinkTo
                @route="authenticated.reimagined.plan"
                class="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium flex items-center gap-1"
              >
                View plan
                <PhArrowRight @size={{14}} />
              </LinkTo>
            </div>

            {{#if this.employee.targetRole}}
              <div class="mb-4">
                <div class="flex items-center justify-between mb-2">
                  <div>
                    <Text
                      @intent="muted"
                      class="text-xs uppercase tracking-wide dark:text-gray-400"
                    >Current Role</Text>
                    <div
                      class="font-medium text-gray-900 dark:text-white"
                    >{{this.employee.currentRole.title}}</div>
                  </div>
                  <PhArrowRight @size={{20}} class="text-gray-400" />
                  <div class="text-right">
                    <Text
                      @intent="muted"
                      class="text-xs uppercase tracking-wide dark:text-gray-400"
                    >Target Role</Text>
                    <div
                      class="font-medium text-violet-600 dark:text-violet-400"
                    >{{this.employee.targetRole.title}}</div>
                  </div>
                </div>

                <div class="mt-4">
                  <div class="flex justify-between text-sm mb-1">
                    <span class="text-gray-600 dark:text-gray-400">Progress</span>
                    <span
                      class="font-medium text-gray-900 dark:text-white"
                    >{{this.progressPercent}}%</span>
                  </div>
                  <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all"
                      style={{this.progressStyle}}
                    ></div>
                  </div>
                </div>
              </div>
            {{else}}
              <div class="text-center py-6">
                <div
                  class="p-3 bg-violet-100 dark:bg-violet-900/50 rounded-full w-14 h-14 mx-auto mb-3 flex items-center justify-center"
                >
                  <PhTarget @size={{28}} class="text-violet-600 dark:text-violet-400" />
                </div>
                <Heading
                  @level={{3}}
                  class="text-base font-semibold text-gray-900 dark:text-white mb-1"
                >Set a career goal</Heading>
                <Text @intent="muted" class="text-sm mb-4 dark:text-gray-400">Discover roles that
                  match your skills and aspirations</Text>
                <LinkTo @route="authenticated.reimagined.explore">
                  <Button @intent="primary">Explore Careers</Button>
                </LinkTo>
              </div>
            {{/if}}

            <div
              class="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 min-w-0"
            >
              <LinkTo
                @route="authenticated.reimagined.skills"
                class="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-center"
              >
                <PhLightbulb @size={{22}} class="text-amber-500 mb-1" />
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Skills</span>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.recommendations"
                class="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-center"
              >
                <PhGraduationCap @size={{22}} class="text-blue-500 mb-1" />
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Learn</span>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.advisor"
                class="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-center"
              >
                <PhRobot @size={{22}} class="text-violet-500 mb-1" />
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">AI Advisor</span>
              </LinkTo>
            </div>
          </div>
        </DataIndicator>

        <MomentumPanel
          @score={{this.score}}
          @companyName={{this.employerName}}
          @streakDays={{14}}
        />
      </div>

      {{! FINANCIAL TOOLS - Quick access to key features }}
      <div class="mb-6">
        <SectionHeader
          @icon={{PhCalculator}}
          @title="Financial Tools"
          @iconClass="text-blue-600"
          @iconSize={{24}}
        />
        <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <ToolCard
            @route="authenticated.reimagined.loans"
            @icon={{PhBank}}
            @title="Debt Dashboard"
            @subtitle="Track your loans"
            @color="indigo"
          />
          <ToolCard
            @route="authenticated.reimagined.benefits"
            @icon={{PhGraduationCap}}
            @title="Benefits Hub"
            @subtitle="Education benefits"
            @color="violet"
          />
          <ToolCard
            @route="authenticated.reimagined.plan.strategy"
            @icon={{PhTarget}}
            @title="Strategy Finder"
            @subtitle="Best repayment path"
            @color="teal"
          />
          <ToolCard
            @route="authenticated.reimagined.coaches"
            @icon={{PhChatCircleDots}}
            @title="Loan Coaches"
            @subtitle="Expert guidance"
            @color="emerald"
          />
          <ToolCard
            @route="authenticated.reimagined.glossary"
            @icon={{PhBookOpen}}
            @title="Glossary"
            @subtitle="Learn the terms"
            @color="blue"
          />
          <ToolCard
            @route="authenticated.idr.dashboard"
            @icon={{PhScales}}
            @title="Income-Driven"
            @subtitle="Lower payments"
            @color="amber"
          />
          <ToolCard
            @route="authenticated.planning-for-college.529-tools"
            @icon={{PhPiggyBank}}
            @title="529 Plans"
            @subtitle="Save for education"
            @color="pink"
          />
        </div>
      </div>

      {{! EMPLOYER APPRECIATION BANNER }}
      <div class="mt-6 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div class="flex items-center gap-4">
            <div class="p-3 bg-white/20 rounded-lg">
              <PhBuildings @size={{28}} />
            </div>
            <div>
              <Heading @level={{3}} class="text-white text-lg font-semibold">
                {{this.employerName}}
                has invested
                {{this.formatCurrency this.financials.employerHelpLifetime}}
                in you
              </Heading>
              <Text class="text-emerald-100">
                Through student loan payments, tuition assistance, and tax-free benefits
              </Text>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
